import React, { FC, Fragment } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { DurationFilter } from '../../../utils/types';
import { HiSelector, HiCheck } from 'react-icons/hi';

type Props = {
  meetingLengths: DurationFilter[];
  handleMeetingLengthsChange: (position: number) => void;
};

const MeetingLengthsComboBox: FC<Props> = ({
  meetingLengths,
  handleMeetingLengthsChange,
}) => {
  //Handleclick and set selectedOption
  const handleClick = (position: number) => {
    handleMeetingLengthsChange(position);
  };

  const formatLabel = (meetingLengths: DurationFilter[]) => {
    const tempMeetingLengths = meetingLengths.filter((ml) => ml.isChecked);
    if (tempMeetingLengths.length === 0) return 'Please choose';
    return tempMeetingLengths.map(
      (ml, idx) =>
        ml.duration + ` min${idx !== tempMeetingLengths.length - 1 ? ',' : ''} `
    );
  };

  return (
    <div className="w-full">
      <Combobox value={meetingLengths} multiple>
        <div className="relative mt-1 active:outline-none">
          <div className="relative w-full overflow-hidden text-left bg-white rounded-lg shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
            <div className="block m-2 lg:hidden">
              <Combobox.Button>
                <Combobox.Label className="whitespace-nowrap">
                  Meeting Lengths: {formatLabel(meetingLengths)}
                </Combobox.Label>
              </Combobox.Button>
            </div>
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <HiSelector
                className="w-5 h-5 text-gray-400"
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Combobox.Options className="absolute z-10 w-full max-w-full py-1 mt-1 overflow-scroll text-base bg-white rounded-md shadow-lg max-h-64 h-fit ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {meetingLengths.map((option, idx) => (
                <Combobox.Option
                  key={idx}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 px-3 ${
                      active
                        ? 'bg-confirmed-secondary text-white'
                        : 'text-gray-900'
                    }`
                  }
                  value={option}
                  onClick={() => handleClick(idx)}
                >
                  {({ selected, active }) => (
                    <div className="flex flex-row justify-between whitespace-no-wrap">
                      <span
                        className={`block truncate ${
                          selected ? 'font-medium' : 'font-normal'
                        }`}
                      >
                        {option.duration}
                      </span>
                      {option.isChecked ? (
                        <span
                          className={`absolute inset-y-0 right-3 flex items-center pl-3 ${
                            active ? 'text-white' : 'text-confirmed-secondary'
                          }`}
                        >
                          <HiCheck className="w-5 h-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </div>
                  )}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
};

export default MeetingLengthsComboBox;
